import { useEffect, useState } from "react";

export type Orientation = "portrait" | "landscape";

export interface OrientationLockOptions {
  /**
   * Routen, die von der Portrait-Erzwingung ausgenommen sind (selten; leer = alle Routen unterliegen dem Lock auf Mobilgeräten)
   */
  excludedRoutes?: string[];
  /**
   * Ob die Orientierung gesperrt werden soll
   * @default true
   */
  enabled?: boolean;
}

/**
 * Prüft ob die App als PWA (Standalone) läuft
 */
function isPWA(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

/**
 * Erkennt, ob die App in einem automatisierten Browser-Kontext läuft (z.B. Playwright/MCP).
 * In diesem Modus können Viewport/Screenshot-Operationen kurzzeitig "Landscape" triggern und
 * damit das Portrait-Lock Overlay fälschlich einblenden.
 */
function isAutomationBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { webdriver?: boolean };
  return nav.webdriver === true;
}

/**
 * Prüft ob es sich um ein mobiles Gerät handelt
 * (Tablet, Smartphone, etc.)
 *
 * WICHTIG: Desktop-Browser werden NICHT als mobil erkannt,
 * auch wenn sie Touch-Support haben oder in einem kleinen Fenster laufen.
 */
function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;

  // In Automation-Kontexten NIE als "mobil" behandeln (stabilisiert MCP/Playwright Tests)
  if (isAutomationBrowser()) return false;

  // Prüfe User-Agent auf mobile Geräte (primärer Indikator)
  const userAgent =
    window.navigator.userAgent ||
    window.navigator.vendor ||
    (window as any).opera;
  const mobileRegex =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  // Wenn User-Agent eindeutig mobil ist → return true
  if (mobileRegex.test(userAgent)) {
    return true;
  }

  // Wenn PWA installiert ist → wahrscheinlich mobil (kann aber auch Desktop sein)
  // Daher kombinieren wir mit User-Agent-Prüfung
  if (isPWA()) {
    // Wenn PWA aber User-Agent zeigt Desktop → nicht als mobil behandeln
    const desktopRegex = /windows|macintosh|linux/i;
    if (!desktopRegex.test(userAgent)) {
      return true; // PWA ohne Desktop-User-Agent → wahrscheinlich mobil
    }
  }

  // Desktop-Browser werden NICHT als mobil erkannt
  return false;
}

/**
 * Versucht die Screen Orientation API zu nutzen, um Portrait-Modus zu sperren.
 * Funktioniert nur in bestimmten Kontexten (PWA, Fullscreen, etc.)
 *
 * @param retryCount Anzahl der Versuche (für Retry-Logik)
 * @returns Promise<boolean> - true wenn Lock erfolgreich war oder versucht wurde
 */
async function tryLockOrientation(retryCount = 0): Promise<boolean> {
  if (typeof window === "undefined" || !("screen" in window)) {
    return false;
  }

  const screen = window.screen as Screen & {
    orientation?: {
      lock?: (orientation: string) => Promise<void>;
      type?: string;
    };
  };

  // Screen Orientation API (moderne Browser)
  if (screen.orientation?.lock) {
    try {
      await screen.orientation.lock("portrait");
      console.log("[OrientationLock] ✅ Portrait-Modus erfolgreich gesperrt");
      return true;
    } catch (error: any) {
      // In PWA sollte es funktionieren - versuche es erneut nach User-Interaktion
      if (isPWA() && retryCount < 3) {
        console.debug(
          `[OrientationLock] ⏳ Lock fehlgeschlagen (Versuch ${retryCount + 1}/3), versuche erneut...`
        );
        // Warte kurz und versuche es erneut
        await new Promise(resolve => setTimeout(resolve, 500));
        return tryLockOrientation(retryCount + 1);
      }

      // Nur loggen wenn nicht in PWA (dann ist es erwartet)
      if (!isPWA()) {
        console.debug(
          "[OrientationLock] ℹ️ Screen Orientation API nicht verfügbar (Browser-Tab/Desktop - erwartet)"
        );
      } else {
        console.warn(
          "[OrientationLock] ⚠️ Portrait-Lock fehlgeschlagen in PWA:",
          error.message
        );
      }
      return false;
    }
  }

  // Legacy API (veraltet, aber manchmal noch verfügbar)
  if (
    "lockOrientation" in screen ||
    "mozLockOrientation" in screen ||
    "msLockOrientation" in screen
  ) {
    const lockFn =
      (screen as any).lockOrientation ||
      (screen as any).mozLockOrientation ||
      (screen as any).msLockOrientation;
    try {
      lockFn("portrait");
      console.log("[OrientationLock] ✅ Legacy API: Portrait gesperrt");
      return true;
    } catch (error) {
      console.debug("[OrientationLock] Legacy API nicht verfügbar:", error);
    }
  }

  return false;
}

/**
 * Hook zur Erkennung und Erzwingung von Portrait-Modus
 *
 * Kombiniert mehrere Ansätze:
 * 1. Screen Orientation API (wenn verfügbar, besonders in PWA)
 * 2. Overlay bei Landscape-Erkennung (Fallback)
 * 3. CSS-Regeln zur Blockierung
 *
 * Verwendung:
 * ```tsx
 * const { orientation, isPortrait, isLandscape } = useOrientationLock({
 *   excludedRoutes: [],
 *   enabled: true
 * });
 * ```
 *
 * @param options Konfiguration für Orientation-Lock
 * @returns Aktuelle Orientierung und Helper-Werte
 */
export function useOrientationLock(options: OrientationLockOptions = {}): {
  orientation: Orientation;
  isPortrait: boolean;
  isLandscape: boolean;
  shouldBlock: boolean;
} {
  const { excludedRoutes = [], enabled = true } = options;
  const effectiveEnabled = enabled && !isAutomationBrowser();
  const [orientation, setOrientation] = useState<Orientation>(() => {
    // Initial check
    if (typeof window === "undefined") return "portrait";
    // Auf Desktop immer "portrait" zurückgeben (keine Orientierungssperre)
    if (!isMobileDevice()) return "portrait";
    return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
  });

  // Helper-Funktion: Prüft ob aktuelle Route ausgenommen ist
  const isRouteExcluded = (): boolean => {
    if (excludedRoutes.length === 0) return false;
    const currentPath = window.location.pathname;
    return excludedRoutes.some(route => currentPath.startsWith(route));
  };

  // State: Track ob Lock erfolgreich war
  const [isLocked, setIsLocked] = useState(false);

  // Versuche Screen Orientation API aggressiv zu nutzen (besonders in PWA)
  useEffect(() => {
    if (!effectiveEnabled) {
      setIsLocked(false);
      return;
    }

    let mounted = true;

    // Initialer Versuch
    const attemptLock = async () => {
      const locked = await tryLockOrientation();
      if (mounted) {
        setIsLocked(locked);
      }
    };

    // Versuche sofort
    attemptLock();

    // In PWA: Versuche auch nach User-Interaktion (kann helfen)
    if (isPWA()) {
      const handleUserInteraction = async () => {
        if (!isLocked) {
          const locked = await tryLockOrientation();
          if (mounted && locked) {
            setIsLocked(true);
            // Entferne Listener nach erfolgreichem Lock
            window.removeEventListener("touchstart", handleUserInteraction);
            window.removeEventListener("click", handleUserInteraction);
          }
        }
      };

      // Versuche bei User-Interaktion (Touch/Click)
      window.addEventListener("touchstart", handleUserInteraction, {
        once: true,
      });
      window.addEventListener("click", handleUserInteraction, { once: true });

      // Cleanup
      return () => {
        mounted = false;
        window.removeEventListener("touchstart", handleUserInteraction);
        window.removeEventListener("click", handleUserInteraction);
      };
    }

    // Cleanup: Entsperre bei Unmount (falls möglich)
    return () => {
      mounted = false;
      if (typeof window !== "undefined" && "screen" in window) {
        const screen = window.screen as Screen & {
          orientation?: {
            unlock?: () => void;
          };
        };
        try {
          screen.orientation?.unlock?.();
        } catch (error) {
          // Ignoriere Fehler beim Entsperren
        }
      }
    };
  }, [effectiveEnabled, isLocked]);

  // Überwache Orientierung (für Overlay-Fallback)
  // WICHTIG: Wenn Lock erfolgreich war, sollte sich die Orientierung nicht ändern
  useEffect(() => {
    if (!effectiveEnabled) return;

    const checkOrientation = async () => {
      // Wenn Lock erfolgreich war, versuche es erneut zu locken (falls es sich geändert hat)
      if (isLocked && isPWA()) {
        // Versuche erneut zu locken, falls sich die Orientierung geändert hat
        const locked = await tryLockOrientation();
        if (locked && !isLocked) {
          setIsLocked(true);
        }
      }

      // Auf Desktop: Immer "portrait" (keine Orientierungssperre)
      if (!isMobileDevice()) {
        setOrientation("portrait");
        return;
      }

      // Bessere Orientierungserkennung: Nutze Screen Orientation API wenn verfügbar,
      // sonst Fallback auf width/height
      let isLandscape = false;

      if (
        typeof window !== "undefined" &&
        "screen" in window &&
        (window.screen as any).orientation?.type
      ) {
        // Screen Orientation API (genauer)
        const orientationType = (window.screen as any).orientation.type;
        isLandscape =
          orientationType === "landscape-primary" ||
          orientationType === "landscape-secondary";
      } else {
        // Fallback: width > height
        isLandscape = window.innerWidth > window.innerHeight;
      }

      setOrientation(isLandscape ? "landscape" : "portrait");
    };

    // Initial check
    checkOrientation();

    // Event listeners
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    // Screen Orientation API Events (wenn verfügbar)
    if (
      typeof window !== "undefined" &&
      "screen" in window &&
      (window.screen as any).orientation
    ) {
      (window.screen as any).orientation.addEventListener(
        "change",
        checkOrientation
      );
    }

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
      if (
        typeof window !== "undefined" &&
        "screen" in window &&
        (window.screen as any).orientation
      ) {
        (window.screen as any).orientation.removeEventListener(
          "change",
          checkOrientation
        );
      }
    };
  }, [effectiveEnabled, isLocked]);

  // Setze body-Klasse für CSS-Regeln
  useEffect(() => {
    if (!effectiveEnabled) {
      document.body.classList.remove("portrait-lock-active");
      return;
    }

    const shouldBlockNow = orientation === "landscape" && !isRouteExcluded();

    if (shouldBlockNow) {
      document.body.classList.add("portrait-lock-active");
    } else {
      document.body.classList.remove("portrait-lock-active");
    }

    return () => {
      document.body.classList.remove("portrait-lock-active");
    };
  }, [effectiveEnabled, orientation, excludedRoutes]);

  // Prüfe ob aktuelle Route ausgenommen ist
  // WICHTIG: Wenn Lock erfolgreich war, zeige KEIN Overlay (Rotation ist blockiert)
  // WICHTIG: Nur auf mobilen Geräten aktivieren - Desktop hat keine Orientierung
  // WICHTIG: Auf Desktop-Browsern NIE das Overlay anzeigen, auch nicht bei schmalen Fenstern
  // (Auch wenn orientation fälschlicherweise "landscape" ist, z.B. bei breiten Desktop-Fenstern)
  if (!effectiveEnabled || !isMobileDevice()) {
    // Auf Desktop-Browsern: Overlay niemals anzeigen
    return {
      orientation: "portrait",
      isPortrait: true,
      isLandscape: false,
      shouldBlock: false,
    };
  }

  const shouldBlock =
    effectiveEnabled &&
    !isLocked && // Kein Overlay wenn Lock erfolgreich
    orientation === "landscape" &&
    !isRouteExcluded();

  return {
    orientation,
    isPortrait: orientation === "portrait",
    isLandscape: orientation === "landscape",
    shouldBlock,
  };
}
